import {
  Shield, Smartphone, Zap, Target, Users, CheckCircle,
  ChevronDown, ChevronUp, Star, Lock, Clock,
  AlertTriangle, BookOpen, Languages, CreditCard, Monitor, Cloud, Radar,
} from 'lucide-react';
import { useState } from 'react';
import { useLanguage } from './i18n/useLanguage';
import { type Lang, LANG_LABELS, type Translations } from './i18n/translations';
import { posts } from './blog/posts';
import { LandingStatus, LANDING_STATUS_HEADINGS } from './LandingStatus';
import { DOWNLOADABLE_APP_URL } from '../config/appLinks';

const BENEFIT_ICONS = [<Zap size={22} />, <Users size={22} />, <Target size={22} />, <Shield size={22} />, <Radar size={22} />, <Smartphone size={22} />];
const TRUST_ICONS = [<Lock size={20} />, <Monitor size={20} />, <Clock size={20} />, <CreditCard size={20} />];

function FaqItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="faq-item" onClick={() => setOpen((o) => !o)}>
      <div className="faq-q">
        <span>{q}</span>
        {open ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
      </div>
      {open && <p className="faq-a">{a}</p>}
    </div>
  );
}

function encode(data: Record<string, string>) {
  return Object.keys(data).map((k) => `${encodeURIComponent(k)}=${encodeURIComponent(data[k])}`).join('&');
}

function LangSelector({ lang, setLang }: { lang: Lang; setLang: (l: Lang) => void }) {
  const [open, setOpen] = useState(false);
  const langs = Object.keys(LANG_LABELS) as Lang[];
  return (
    <div className="lp-lang-wrap">
      <button className="lp-lang-btn" onClick={() => setOpen((o) => !o)} aria-label="Select language">
        {LANG_LABELS[lang]}
      </button>
      {open && (
        <div className="lp-lang-menu">
          {langs.map((l) => (
            <button
              key={l}
              className={`lp-lang-opt${l === lang ? ' active' : ''}`}
              onClick={() => { setLang(l); setOpen(false); }}
            >
              {LANG_LABELS[l]}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ContactForm({ t }: { t: ReturnType<typeof useLanguage>['t'] }) {
  const c = t.contact;
  const [form, setForm] = useState({ nombre: '', email: '', asunto: 'consulta', mensaje: '' });
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('sending');
    try {
      await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: encode({ 'form-name': 'contacto', ...form }),
      });
      setStatus('sent');
    } catch {
      setStatus('error');
    }
  };

  if (status === 'sent') {
    return (
      <div className="contact-success">
        <CheckCircle size={40} />
        <h3>{c.successTitle}</h3>
        <p>{c.successBody}</p>
      </div>
    );
  }

  return (
    <form className="lp-contact-form" onSubmit={submit} name="contacto" data-netlify="true">
      <input type="hidden" name="form-name" value="contacto" />
      <div className="lp-form-row">
        <div className="lp-field">
          <label>{c.fields.name}</label>
          <input type="text" name="nombre" required placeholder={c.namePh} value={form.nombre} onChange={set('nombre')} />
        </div>
        <div className="lp-field">
          <label>{c.fields.email}</label>
          <input type="email" name="email" required placeholder={c.emailPh} value={form.email} onChange={set('email')} />
        </div>
      </div>
      <div className="lp-field">
        <label>{c.fields.subject}</label>
        <select name="asunto" value={form.asunto} onChange={set('asunto')}>
          <option value="consulta">{c.subjects.consulta}</option>
          <option value="soporte">{c.subjects.soporte}</option>
          <option value="facturacion">{c.subjects.facturacion}</option>
          <option value="baneo">{c.subjects.baneo}</option>
          <option value="otro">{c.subjects.otro}</option>
        </select>
      </div>
      <div className="lp-field">
        <label>{c.fields.message}</label>
        <textarea name="mensaje" required rows={5} placeholder={c.messagePh} value={form.mensaje} onChange={set('mensaje')} />
      </div>
      {status === 'error' && <p className="lp-form-error">{c.errorMsg}</p>}
      <button type="submit" className="lp-btn-primary" disabled={status === 'sending'}>
        {status === 'sending' ? c.submitting : c.submit}
      </button>
    </form>
  );
}

// Aviso "te escribimos cuando el Cloud abra": formulario Netlify pasivo
// (cloud-waitlist). Independiente de la waitlist automática del hosted.
function CloudNotifyForm({ t }: { t: Translations['cloudNotify'] }) {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('sending');
    try {
      await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: encode({ 'form-name': 'cloud-waitlist', email }),
      });
      setStatus('sent');
    } catch {
      setStatus('error');
    }
  };

  if (status === 'sent') {
    return (
      <div className="lp-cloud-notify-ok">
        <CheckCircle size={20} />
        <div>
          <strong>{t.successTitle}</strong>
          <p>{t.successBody}</p>
        </div>
      </div>
    );
  }

  return (
    <form className="lp-cloud-notify" onSubmit={submit} name="cloud-waitlist" data-netlify="true">
      <input type="hidden" name="form-name" value="cloud-waitlist" />
      <input
        type="email"
        name="email"
        required
        placeholder={t.emailPh}
        value={email}
        onChange={(e) => setEmail(e.target.value)}
      />
      <button type="submit" className="lp-btn-plan-secondary" disabled={status === 'sending' || !email}>
        {status === 'sending' ? t.submitting : t.submit}
      </button>
      {status === 'error' && <p className="lp-form-error">{t.errorMsg}</p>}
    </form>
  );
}

// Lista de la beta (form Netlify "beta-list"): captura a los visitantes que no
// llegan a registrarse — los que entran a la beta ya dejan email en el alta.
// La sección solo se muestra durante la beta (date-gated).
function BetaListForm({ t }: { t: Translations['betaList'] }) {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('sending');
    try {
      await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: encode({ 'form-name': 'beta-list', email }),
      });
      setStatus('sent');
    } catch {
      setStatus('error');
    }
  };

  if (status === 'sent') {
    return (
      <div className="lp-cloud-notify-ok">
        <CheckCircle size={20} />
        <div>
          <strong>{t.successTitle}</strong>
          <p>{t.successBody}</p>
        </div>
      </div>
    );
  }

  return (
    <form className="lp-cloud-notify lp-betalist-form" onSubmit={submit} name="beta-list" data-netlify="true">
      <input type="hidden" name="form-name" value="beta-list" />
      <input
        type="email"
        name="email"
        required
        placeholder={t.emailPh}
        value={email}
        onChange={(e) => setEmail(e.target.value)}
      />
      <button type="submit" className="lp-btn-primary" disabled={status === 'sending' || !email}>
        {status === 'sending' ? t.submitting : t.submit}
      </button>
      {status === 'error' && <p className="lp-form-error">{t.errorMsg}</p>}
    </form>
  );
}

// Redes oficiales (marca unificada @whitebotapp). Logos reales en SVG inline
// (lucide no trae el logo nuevo de X). Dan prueba social / credibilidad.
const X_URL = 'https://x.com/whitebotapp';
const YOUTUBE_URL = 'https://www.youtube.com/@whitebotapp';

// Tutorial de instalación completo (2026-08-07): de cero a bot en marcha.
// Enlazado bajo el CTA del descargable — es la objeción nº1 ("¿sabré
// instalarlo?") respondida en el punto exacto de la decisión. Etiqueta
// localizada aquí mismo (no toca translations.ts; fallback EN).
const INSTALL_VIDEO_URL = 'https://youtu.be/G774G5X1P0Q';
const INSTALL_VIDEO_LABEL: Record<string, string> = {
  es: '▶ Mira la instalación completa (vídeo, 7 min)',
  en: '▶ Watch the full install (video, 7 min)',
  fr: '▶ Voir l’installation complète (vidéo, 7 min)',
  de: '▶ Komplette Installation ansehen (Video, 7 Min.)',
  pt: '▶ Veja a instalação completa (vídeo, 7 min)',
};

function SocialLinks({ className = '' }: { className?: string }) {
  return (
    <div className={`lp-social ${className}`}>
      <a href={X_URL} target="_blank" rel="noopener" aria-label="White Bot on X">
        <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden="true">
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
        </svg>
      </a>
      <a href={YOUTUBE_URL} target="_blank" rel="noopener" aria-label="White Bot on YouTube">
        <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor" aria-hidden="true">
          <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
        </svg>
      </a>
    </div>
  );
}

// BLOQUEANTE DE LANZAMIENTO — clip del Auto Shield interceptando un ataque en
// vivo: el activo de mayor conversión del sitio. Cuando exista, poner aquí su
// URL (mp4/webm en public/) y la sección DEMO aparece sola.
// <!-- CLIP AUTO-SHIELD PENDIENTE -->
const AUTO_SHIELD_CLIP_URL: string = '';

export function LandingPage() {
  const { lang, t, setLang } = useLanguage();
  // Beta gratuita del descargable: misma fecha que domain/beta.ts de
  // downloadable_model/web y que DOWNLOADABLE_BETA_UNTIL en Netlify.
  // Comprobación en runtime — el 16/8 la landing vuelve sola a los textos
  // normales sin redeploy.
  const beta = Date.now() < Date.parse('2026-12-31T23:59:59Z') ? t.beta : undefined;

  return (
    <div className="lp">
      {/* NAV */}
      <nav className="lp-nav">
        <span className="lp-nav-logo"><img src="/logo.png" alt="White Bot" /></span>
        <div className="lp-nav-links">
          <a href="#productos">{t.nav.products}</a>
          <a href="#como-funciona">{t.nav.how}</a>
          <a href="#estado">{t.footer.links.status}</a>
          <a href="#faq">{t.nav.faq}</a>
          <a href="#contacto">{t.nav.contact}</a>
          <LangSelector lang={lang} setLang={setLang} />
          <a href={DOWNLOADABLE_APP_URL} className="lp-btn-outline" rel="noopener">{t.nav.enter}</a>
        </div>
      </nav>

      {/* HERO */}
      <section className="lp-hero">
        <div className="lp-hero-inner">
          <div className="lp-badge">{beta ? beta.downloadable.badge : t.hero.badge}</div>
          {/* translate="no": los traductores automáticos (Chrome) no deben tocar la marca */}
          <h1 className="lp-h1" translate="no">White Bot</h1>
          <p className="lp-tagline">
            {t.hero.tagline[0]}<br />
            {t.hero.tagline[1]}<br />
            <strong>{t.hero.tagline[2]}</strong>
          </p>
          <div className="lp-hero-ctas">
            <a href={DOWNLOADABLE_APP_URL} className="lp-btn-primary" rel="noopener">{beta?.hero.cta1 ?? t.hero.cta1}</a>
            {/* Un solo CTA por sección (reposicionamiento 2026-07): el ancla
                "Ver cómo funciona" diluía la acción — restaurar si se decide:
                <a href="#como-funciona" className="lp-btn-ghost">{t.hero.cta2}</a> */}
          </div>
        </div>
        <div className="lp-hero-visual">
          {/* Demo real del bot (YouTube) — sustituye al mockup ilustrativo:
              prueba visual > maqueta. */}
          <div className="lp-hero-video">
            <iframe
              src="https://www.youtube-nocookie.com/embed/dhouCxslNb4?rel=0"
              title="White Bot — Whiteout Survival bot running live"
              loading="lazy"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        </div>
      </section>

      {/* DEMO — clip del Auto Shield (show, don't tell). Oculto hasta que
          exista el clip: ver AUTO_SHIELD_CLIP_URL arriba. */}
      {AUTO_SHIELD_CLIP_URL && (
        <section className="lp-section lp-demo-section">
          <div className="lp-container">
            <video
              className="lp-demo-video"
              src={AUTO_SHIELD_CLIP_URL}
              autoPlay
              muted
              loop
              playsInline
              controls
            />
          </div>
        </section>
      )}

      {/* STATS BAR */}
      <div className="lp-stats-bar">
        <div className="lp-stat-item"><strong>{t.stats.s1v}</strong><span>{t.stats.s1l}</span></div>
        <div className="lp-stat-divider" />
        <div className="lp-stat-item"><strong>{t.stats.s2v}</strong><span>{t.stats.s2l}</span></div>
        <div className="lp-stat-divider" />
        <div className="lp-stat-item"><strong>{t.stats.s3v}</strong><span>{t.stats.s3l}</span></div>
        <div className="lp-stat-divider" />
        <div className="lp-stat-item"><strong>{beta?.stats.s4v ?? t.stats.s4v}</strong><span>{beta ? beta.downloadable.badge : t.stats.s4l}</span></div>
      </div>

      {/* PRODUCTS — Descargable (disponible) + Cloud (en desarrollo) */}
      <section id="productos" className="lp-section lp-products-section">
        <div className="lp-container">
          <h2 className="lp-section-title" style={{ color: '#fff' }}>{t.products.title}</h2>
          <p className="lp-section-sub" style={{ color: '#94a3b8' }}>{t.products.sub}</p>
          <div className="lp-products-grid">
            <div className="lp-plan-card lp-plan-featured lp-product-card">
              <div className="lp-plan-badge-feat lp-badge-available">{beta?.downloadable.badge ?? t.products.downloadable.badge}</div>
              <div className="lp-product-icon"><Monitor size={24} /></div>
              <div className="lp-plan-name">{t.products.downloadable.name}</div>
              <p className="lp-product-desc">{t.products.downloadable.desc}</p>
              <div className="lp-plan-divider" />
              <ul className="lp-plan-features">
                {/* Beta (pago aparcado 2026-08-02): se omite el último feature
                    (el del trial de pago) mientras dure la beta; el resto son
                    funciones reales sin referencia a pago. */}
                {(beta
                  ? t.products.downloadable.features.slice(0, -1)
                  : t.products.downloadable.features
                ).map((f) => <li key={f}><CheckCircle size={14} />{f}</li>)}
              </ul>
              <div className="lp-product-price">{beta ? beta.downloadable.badge : t.products.downloadable.priceNote}</div>
              <a href={DOWNLOADABLE_APP_URL} className="lp-btn-primary lp-btn-plan" rel="noopener">{beta?.downloadable.cta ?? t.products.downloadable.cta}</a>
              <a href={INSTALL_VIDEO_URL} className="lp-video-link" target="_blank" rel="noopener">
                {INSTALL_VIDEO_LABEL[lang] ?? INSTALL_VIDEO_LABEL.en}
              </a>
            </div>
            <div id="cloud" className="lp-plan-card lp-product-card lp-product-cloud">
              <span className="lp-plan-badge-soon lp-plan-badge-top">{t.products.cloud.badge}</span>
              <div className="lp-product-icon lp-product-icon-cloud"><Cloud size={24} /></div>
              <div className="lp-plan-name">{t.products.cloud.name}</div>
              <p className="lp-product-desc">{t.products.cloud.desc}</p>
              <div className="lp-plan-divider" />
              <ul className="lp-plan-features">
                {t.products.cloud.features.map((f) => <li key={f}><CheckCircle size={14} />{f}</li>)}
              </ul>
              <div className="lp-product-price">{t.products.cloud.priceNote}</div>
              <p className="lp-cloud-notify-label">{t.products.cloud.notifyLabel}</p>
              <CloudNotifyForm t={t.cloudNotify} />
              {/* Acceso a la beta privada del hosted: única puerta al dashboard
                  hosted. Los usuarios beta (a los que el admin ya provisionó un
                  slot) entran por aquí; el resto acaba en el descargable. */}
              <a href="/dashboard" className="lp-cloud-beta-access">{t.products.cloud.betaAccess}</a>
            </div>
          </div>
        </div>
      </section>

      {/* FOR WHOM */}
      <section className="lp-section lp-forwhom-section">
        <div className="lp-container">
          <div className="lp-forwhom-grid">
            <div className="lp-forwhom-card lp-forwhom-yes">
              <div className="lp-forwhom-header"><CheckCircle size={22} /><h3>{t.forwhom.yes.title}</h3></div>
              <ul>{t.forwhom.yes.items.map((item) => <li key={item}>{item}</li>)}</ul>
            </div>
            <div className="lp-forwhom-card lp-forwhom-no">
              <div className="lp-forwhom-header"><AlertTriangle size={22} /><h3>{t.forwhom.no.title}</h3></div>
              <ul>{t.forwhom.no.items.map((item) => <li key={item} dangerouslySetInnerHTML={{ __html: item }} />)}</ul>
            </div>
          </div>
        </div>
      </section>

      {/* COMPARISON */}
      <section className="lp-section lp-compare-section">
        <div className="lp-container">
          <h2 className="lp-section-title">{t.compare.title}</h2>
          <p className="lp-section-sub">{t.compare.sub}</p>
          <div className="lp-compare-table-wrap">
            <table className="lp-compare-table">
              <thead>
                <tr>
                  {t.compare.headers.map((h, i) => (
                    <th key={i} className={i === 2 ? 'lp-th-bot' : ''}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {t.compare.rows.map(([label, manual, bot]) => (
                  <tr key={label}>
                    <td>{label}</td>
                    <td>{manual}</td>
                    <td className="lp-td-bot">{bot}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="lp-section lp-benefits-section">
        <div className="lp-container">
          <h2 className="lp-section-title">{t.benefits.title}</h2>
          <p className="lp-section-sub">{t.benefits.sub}</p>
          <div className="lp-benefits-grid">
            {t.benefits.items.map((b, i) => (
              <div key={b.title} className="lp-benefit-card">
                <div className="lp-benefit-icon">{BENEFIT_ICONS[i]}</div>
                <h3>{b.title}</h3>
                <p>{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="como-funciona" className="lp-section lp-steps-section">
        <div className="lp-container">
          <h2 className="lp-section-title">{t.steps.title}</h2>
          <p className="lp-section-sub">{t.steps.sub}</p>
          <div className="lp-steps">
            {/* Beta: el primer paso es el del trial en los 5 idiomas y se
                sustituye por el de la beta mientras dure */}
            {(beta ? [beta.stepTrial, ...t.steps.items.slice(1)] : t.steps.items).map((s, i) => (
              <div key={s.title} className="lp-step">
                <div className="lp-step-num">{String(i + 1).padStart(2, '0')}</div>
                <h3>{s.title}</h3>
                <p>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* STATUS (reemplaza a Precios: los precios ya viven en la sección
          Productos, más arriba. Aquí damos visibilidad al estado en vivo, que
          antes solo era un enlace discreto del footer). */}
      <section id="estado" className="lp-section lp-pricing-section lp-status-section">
        <div className="lp-container">
          <h2 className="lp-section-title" style={{ color: '#fff' }}>{LANDING_STATUS_HEADINGS[lang].title}</h2>
          <p className="lp-section-sub" style={{ color: '#94a3b8' }}>{LANDING_STATUS_HEADINGS[lang].sub}</p>
          <LandingStatus lang={lang} />
        </div>
      </section>

      {/* BETA LIST — captura de email antes del fin de la beta. OCULTA
          (2026-08-02): estrategia de pago aparcada; su premisa "los planes de
          pago empiezan el 16" ya no aplica. Reactivar: quitar el `false &&`. */}
      {false && beta && (
        <section className="lp-section lp-betalist-section">
          <div className="lp-container lp-betalist-inner">
            <div className="lp-betalist-icon"><Shield size={26} /></div>
            <h2 className="lp-section-title">{t.betaList.title}</h2>
            <p className="lp-section-sub">{t.betaList.body}</p>
            <BetaListForm t={t.betaList} />
            <p className="lp-betalist-support">{t.betaList.supportNote}</p>
          </div>
        </section>
      )}

      {/* TRUST */}
      <section className="lp-section lp-trust-section">
        <div className="lp-container">
          <h2 className="lp-section-title">{t.trust.title}</h2>
          <p className="lp-section-sub">{t.trust.sub}</p>
          <div className="lp-trust-grid">
            {t.trust.items.map((item, i) => (
              <div key={item.title} className="lp-trust-card">
                <div className="lp-trust-icon">{TRUST_ICONS[i]}</div>
                <div><h3>{item.title}</h3><p>{item.desc}</p></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* RISK */}
      <section className="lp-section lp-risk-section">
        <div className="lp-container">
          <div className="lp-risk-card">
            <div className="lp-risk-header"><AlertTriangle size={22} /><h3>{t.risk.title}</h3></div>
            <p dangerouslySetInnerHTML={{ __html: t.risk.p1 }} />
            <p dangerouslySetInnerHTML={{ __html: t.risk.p2 }} />
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section id="contacto" className="lp-section lp-contact-section">
        <div className="lp-container lp-contact-inner">
          <div className="lp-contact-info">
            <h2 className="lp-section-title" style={{ textAlign: 'left' }}>{t.contact.title}</h2>
            <p>{t.contact.sub}</p>
            <div className="lp-contact-channels">
              <a href="mailto:whitebot.admin@gmail.com" className="lp-channel">✉ whitebot.admin@gmail.com</a>
            </div>
            <SocialLinks className="lp-social-contact" />
          </div>
          <div className="lp-contact-form-wrap">
            <ContactForm t={t} />
          </div>
        </div>
      </section>

      {/* BLOG PREVIEW */}
      <section className="lp-section lp-blog-section">
        <div className="lp-container">
          <div className="lp-blog-header">
            <div>
              <h2 className="lp-section-title" style={{ textAlign: 'left', marginBottom: '8px' }}>{t.blog.sectionTitle}</h2>
              <p className="lp-blog-sub">{t.blog.sectionSub}</p>
            </div>
            <a href="/blog" className="lp-btn-ghost">{t.blog.viewAll}</a>
          </div>
          <div className="lp-blog-grid">
            {posts.map((post) => {
              const content = lang === 'es' ? post.es : post.en;
              return (
                <a key={post.slug} href={`/blog/${post.slug}`} className="lp-blog-card">
                  <div className="lp-blog-card-icon"><BookOpen size={20} /></div>
                  <div className="lp-blog-card-meta">{post.publishedAt} · {post.readingMinutes} min</div>
                  <h3>{content.title}</h3>
                  <p>{content.description}</p>
                  <span className="lp-blog-card-read">{t.blog.readMore}</span>
                </a>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="lp-section lp-faq-section">
        <div className="lp-container lp-faq-inner">
          <h2 className="lp-section-title">{t.faq.title}</h2>
          <div className="lp-faq-list">
            {/* Beta (pago aparcado 2026-08-02): se ocultan las FAQ de pago del
                descargable — prueba de 5 días (idx 7) y cancelar suscripción
                (idx 11); la de White Bot Cloud (idx 12, hosted) se mantiene. Se
                omite también beta.faq (llevaba fecha + precio). */}
            {(beta ? t.faq.items.filter((_, i) => i !== 7 && i !== 11) : t.faq.items).map((f) => <FaqItem key={f.q} q={f.q} a={f.a} />)}
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="lp-footer">
        <div className="lp-container lp-footer-inner">
          <div className="lp-footer-brand">
            <span className="lp-footer-logo">White Bot</span>
            <SocialLinks className="lp-social-footer" />
          </div>
          <nav className="lp-footer-links">
            <a href={DOWNLOADABLE_APP_URL} rel="noopener">{t.footer.links.enter}</a>
            <a href="/blog">{t.footer.links.blog}</a>
            <a href="/status">{t.footer.links.status}</a>
            <a href="#faq">{t.footer.links.faq}</a>
            <a href="#contacto">{t.footer.links.contact}</a>
            <a href={lang === 'es' ? '/privacidad' : '/privacy'}>{t.footer.links.privacy}</a>
            <a href={lang === 'es' ? '/terminos' : '/terms'}>{t.footer.links.terms}</a>
          </nav>
          <p className="lp-footer-copy">{t.footer.copy}</p>
        </div>
      </footer>
    </div>
  );
}
